import type { OrgInvoice } from "@/types/api";

/**
 * What an invoice row says it was for.
 *
 * "Invoice" on every line is no use when the question being asked is why there
 * are three of them this quarter.
 */
export function describeInvoice(inv: OrgInvoice): string {
	if (inv.kind === "seat_topup") {
		const n = inv.seat_quantity ?? 0;
		return `${n} added ${n === 1 ? "seat" : "seats"}`;
	}
	if (inv.kind === "adjustment") return "Adjustment";
	// A flat-fee subscription has no seat count — printing "null seats" or
	// falling back to "0 seats" would both be wrong, and 0 is the more
	// dangerous of the two because it looks like a real number.
	return inv.pricing_model === "flat_fee"
		? "Subscription"
		: `Subscription — ${inv.seat_quantity ?? 0} seats`;
}

export type InvoiceState = "paid" | "cancelled" | "expired" | "overdue" | "due";

/**
 * Collapse status and due date into the one thing an admin needs to know.
 *
 * Unpaid-and-past-due is the only state that demands action, so it is worth
 * separating from "issued", which otherwise covers both an invoice sent this
 * morning and one ignored for a month.
 *
 * A top-up has no due date — it is payable immediately — so a missing due date
 * must never be read as overdue.
 */
export function invoiceState(
	inv: Pick<OrgInvoice, "status" | "due_at">,
	now: Date = new Date(),
): InvoiceState {
	if (inv.status === "paid") return "paid";
	if (inv.status === "cancelled") return "cancelled";
	if (inv.status === "expired") return "expired";
	if (inv.due_at == null) return "due";
	const due = new Date(inv.due_at).getTime();
	if (Number.isNaN(due)) return "due";
	return due < now.getTime() ? "overdue" : "due";
}
